import axios from 'axios';

const API_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');

// Create axios instance
const api = axios.create({
  baseURL: `${API_URL}/api`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Coalesce identical shared GETs mounted by multiple layout components. The
// optional TTL is intended for stable public configuration/menu data; dynamic
// unread counters use ttlMs=0 and only share an in-flight request.
const sharedGets = new Map();
export function sharedGet(url, config = {}, ttlMs = 0) {
  const key = `${url}:${JSON.stringify(config.params || {})}`;
  const now = Date.now();
  const existing = sharedGets.get(key);
  if (existing && (existing.pending || existing.expiresAt > now)) {
    return existing.promise;
  }
  const entry = { pending: true, expiresAt: 0, promise: null };
  entry.promise = api.get(url, config)
    .then((response) => {
      entry.pending = false;
      entry.expiresAt = Date.now() + ttlMs;
      if (ttlMs <= 0) sharedGets.delete(key);
      return response;
    })
    .catch((error) => {
      sharedGets.delete(key);
      throw error;
    });
  sharedGets.set(key, entry);
  return entry.promise;
}

let authGeneration = 0;
let pendingRefresh = null;

// Called by AuthContext on login/logout so previous requests cannot modify a
// new account's tokens or share its refresh operation.
export function invalidateAuthSession() {
  authGeneration += 1;
  pendingRefresh = null;
}

function endAuthSession() {
  invalidateAuthSession();
  localStorage.removeItem('snappipit_token');
  localStorage.removeItem('snappipit_refresh_token');
  localStorage.removeItem('snappipit_user');
  window.location.href = '/login';
}

function refreshAuthSession(generation, refreshToken) {
  if (pendingRefresh?.generation === generation) return pendingRefresh.promise;
  const entry = { generation, promise: null };
  entry.promise = axios.post(`${API_URL}/api/auth/refresh`, {
    refresh_token: refreshToken,
  }).then((response) => {
    if (generation !== authGeneration) throw new Error('Auth session changed');
    const { access_token, refresh_token } = response.data || {};
    if (!access_token || !refresh_token) throw new Error('Invalid token refresh response');
    localStorage.setItem('snappipit_token', access_token);
    localStorage.setItem('snappipit_refresh_token', refresh_token);
    return access_token;
  }).finally(() => {
    // An older session finishing must not clear a newer session's refresh.
    if (pendingRefresh === entry) pendingRefresh = null;
  });
  pendingRefresh = entry;
  return entry.promise;
}

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    config.__authGen = authGeneration;
    config.headers = config.headers || {};
    const token = localStorage.getItem('snappipit_token');
    config.__authToken = token;
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const requestUrl = error.config?.url || '';
    const isLoginRequest = requestUrl.includes('/auth/login');

    // A 401 from the login endpoint means the submitted credentials were
    // rejected. It is not an expired authenticated session, so preserve the
    // backend error for the Login screen instead of refreshing/redirecting.
    if (error.response?.status === 401 && !isLoginRequest) {
      const config = error.config;
      const generation = config?.__authGen;
      if (!config || generation !== authGeneration) return Promise.reject(error);
      if (config.__authRetry || requestUrl.includes('/auth/refresh')) {
        endAuthSession();
        return Promise.reject(error);
      }
      config.__authRetry = true;
      const currentToken = localStorage.getItem('snappipit_token');
      // A delayed 401 may arrive after another request already refreshed.
      if (currentToken && config.__authToken !== currentToken) {
        config.headers.Authorization = `Bearer ${currentToken}`;
        return api(config);
      }
      const refreshToken = localStorage.getItem('snappipit_refresh_token');
      if (refreshToken) {
        try {
          const access_token = await refreshAuthSession(generation, refreshToken);
          if (generation !== authGeneration) return Promise.reject(error);
          error.config.headers.Authorization = `Bearer ${access_token}`;
        } catch (refreshError) {
          if (generation !== authGeneration) return Promise.reject(error);
          const status = refreshError.response?.status;
          if (status === 401 || status === 403) {
            endAuthSession();
          } else {
            // AuthContext can retry verification after network/server recovery.
            error.isTransientAuthFailure = true;
          }
          return Promise.reject(error);
        }
        // Retry through the configured instance, preserving its base URL and
        // interceptors. A retry failure is not a refresh failure.
        return api(config);
      } else {
        endAuthSession();
      }
    }
    return Promise.reject(error);
  }
);

export default api;

// Auth API
export const authAPI = {
  sendOTP: (phone) => api.post('/auth/send-otp', { phone }),
  verifyOTP: (phone, otp) => api.post('/auth/verify-otp', { phone, otp }),
  signup: (phone, password, name, role, email, legal = {}) => api.post('/auth/signup', {
    phone, password, name, role,
    ...(email ? { email } : {}),
    terms_accepted: !!legal.terms_accepted,
    privacy_accepted: !!legal.privacy_accepted,
    ...(legal.psa_accepted ? { psa_accepted: true } : {}),
    ...(legal.terms_version ? { terms_version: legal.terms_version } : {}),
    ...(legal.privacy_version ? { privacy_version: legal.privacy_version } : {}),
    ...(legal.psa_version ? { psa_version: legal.psa_version } : {}),
  }),
  getConfig: () => api.get('/auth/config'),
  login: (phone, password) => api.post('/auth/login', { phone, password }),
  forgotPassword: (email) => api.post('/auth/forgot-password', { email }),
  verifyPasswordResetCode: (email, code) => api.post('/auth/verify-password-reset-code', { email, code }),
  resetPassword: (reset_token, new_password) => api.post('/auth/reset-password', { reset_token, new_password }),
  me: () => api.get('/auth/me'),
  updatePreferences: (data) => api.put('/auth/me/preferences', data),
  logout: () => api.post('/auth/logout'),
  resendVerificationEmail: (email) => api.post('/auth/resend-verification-email', { email }),
};

// Masters API
export const mastersAPI = {
  getMakes: (type) => api.get('/masters/makes', { params: { type } }),
  getModels: (makeId) => api.get('/masters/models', { params: { make_id: makeId } }),
  getCategories: () => api.get('/masters/categories'),
  getServiceCategories: () => api.get('/admin/categories'),
  getPartsCategories: () => api.get('/admin/parts-categories'),
  getCountries: (params) => api.get('/masters/countries', { params }),
  getCities: (countryCode) => api.get('/masters/cities', { params: { country_code: countryCode } }),
  getAreas: (cityId) => api.get('/masters/areas', { params: { city_id: cityId } }),
};

// Vehicles API
export const vehiclesAPI = {
  list: (status = 'active') => api.get('/vehicles', { params: status && status !== 'active' ? { status } : {} }),
  create: (vehicle) => api.post('/vehicles', vehicle),
  get: (id) => api.get(`/vehicles/${id}`),
  update: (id, vehicle) => api.put(`/vehicles/${id}`, vehicle),
  delete: (id) => api.delete(`/vehicles/${id}`),
  setDefault: (id) => api.put(`/vehicles/${id}/default`),
  // Future-facing: checks whether a VIN is already on SnappiPit so the
  // Add-Vehicle flow can surface the upcoming claim / transfer banner.
  // Backend may not have this endpoint yet — callers degrade silently.
  checkVin: (vin) => api.get(`/vehicles/check-vin/${encodeURIComponent(vin)}`),

  // UAT-2 Lifecycle endpoints
  archive: (id, body) => api.post(`/vehicles/${id}/archive`, body),
  restore: (id) => api.post(`/vehicles/${id}/restore`),
  markSold: (id, body) => api.post(`/vehicles/${id}/mark-sold`, body),
  ownershipHistory: (id) => api.get(`/vehicles/${id}/ownership-history`),
  ownershipTimeline: (id) => api.get(`/vehicles/${id}/ownership-timeline`),
  serviceHistory: (id) => api.get(`/vehicles/${id}/service-history`),
  pendingVinConfirmations: () => api.get('/vehicles/vin-confirmations/pending'),
  respondVinConfirmation: (id, decision) =>
    api.post(`/vehicles/vin-confirmations/${id}/respond`, { decision }),
};

// Garages API
export const garagesAPI = {
  search: (params) => api.get('/garages/search', { params }),
  nearby: (lat, lng, radius) => api.get('/garages/nearby', { params: { latitude: lat, longitude: lng, radius_km: radius } }),
  specialists: (params) => api.get('/garages/specialists', { params }),
  get: (id) => api.get(`/garages/${id}`),
  getReviews: (id, page) => api.get(`/garages/${id}/reviews`, { params: { page } }),
};

// Public Provider Profile API (UAT-4) — kind = 'garage' | 'dealer'
export const providerPublicAPI = {
  get: (kind, id) => api.get(`/providers/${kind}/${id}`),
};

// AI Assist API (UAT-4 address detection + existing assist endpoints)
export const aiAPI = {
  parseAddress: (rawText) => api.post('/ai/parse-address', { raw_text: rawText }),
  refineDescription: (text, providerType) =>
    api.post('/ai/refine-description', { text, provider_type: providerType }),
};

// Provider Claim API (identify-&-link onboarding)
export const providerClaimsAPI = {
  getLead: (leadId) => api.get(`/provider-claims/lead/${leadId}`),
  create: (leadId, confirmedLocation = {}) => api.post('/provider-claims', {
    lead_id: leadId,
    ...confirmedLocation,
  }),
  my: () => api.get('/provider-claims/my'),
  withdraw: (claimId) => api.post(`/provider-claims/${claimId}/withdraw`),
};

// Admin Provider Claim Review API (Phase 4)
export const adminProviderClaimsAPI = {
  list: (params) => api.get('/admin/provider-claims', { params }),
  get: (id) => api.get(`/admin/provider-claims/${id}`),
  events: (id) => api.get(`/admin/provider-claims/${id}/events`),
  approve: (id) => api.post(`/admin/provider-claims/${id}/approve`),
  reject: (id, reason) => api.post(`/admin/provider-claims/${id}/reject`, { reason }),
};

// Support System API (UAT-6)
export const supportAPI = {
  getCategories: () => api.get('/support/categories'),
  createTicket: (data) => api.post('/support/tickets', data),
  myTickets: () => api.get('/support/tickets/my'),
  getTicket: (id) => api.get(`/support/tickets/${id}`),
  reply: (id, body) => api.post(`/support/tickets/${id}/reply`, { body }),
  chat: (message, history) => api.post('/support/chat', { message, history }),
  escalate: (data) => api.post('/support/escalate', data),
};

export const adminSupportAPI = {
  listTickets: (params) => api.get('/admin/support/tickets', { params }),
  getTicket: (id) => api.get(`/admin/support/tickets/${id}`),
  reply: (id, body) => api.post(`/admin/support/tickets/${id}/reply`, { body }),
  setStatus: (id, status) => api.put(`/admin/support/tickets/${id}/status`, { status }),
  resolve: (id) => api.post(`/admin/support/tickets/${id}/resolve`),
  close: (id) => api.post(`/admin/support/tickets/${id}/close`),
  addNote: (id, body) => api.post(`/admin/support/tickets/${id}/note`, { body }),
};

// Problem Posts API
export const postsAPI = {
  list: (status) => api.get('/posts', { params: { status_filter: status } }),
  create: (post) => api.post('/posts', post),
  get: (id) => api.get(`/posts/${id}`),
  update: (id, post) => api.put(`/posts/${id}`, post),
  cancel: (id) => api.delete(`/posts/${id}`),
  getResponses: (id) => api.get(`/posts/${id}/responses`),
  shortlistResponse: (postId, responseId) => api.put(`/posts/${postId}/responses/${responseId}/shortlist`),
  acceptResponse: (postId, responseId) => api.put(`/posts/${postId}/responses/${responseId}/accept`),
};

// Bookings API
export const bookingsAPI = {
  list: (status) => api.get('/bookings', { params: { status_filter: status } }),
  create: (booking) => api.post('/bookings', booking),
  get: (id) => api.get(`/bookings/${id}`),
  cancel: (id, reason) => api.put(`/bookings/${id}/cancel`, null, { params: { reason } }),
  getQuotation: (id) => api.get(`/bookings/${id}/quotation`),
  acceptQuotation: (id) => api.put(`/bookings/${id}/quotation/accept`),
  rejectQuotation: (id, notes) => api.put(`/bookings/${id}/quotation/reject`, null, { params: { notes } }),
  getJob: (id) => api.get(`/bookings/${id}/job`),
  getTimeline: (id) => api.get(`/bookings/${id}/timeline`),
};

// Garage Management API
export const garageMgmtAPI = {
  getProfile: () => api.get('/garage/profile'),
  updateProfile: (data) => api.put('/garage/profile', data),
  onboardingStep1: (data) => api.post('/garage/onboarding/step1', data),
  onboardingStep2: (data) => api.post('/garage/onboarding/step2', data),
  onboardingStep3: (data) => api.post('/garage/onboarding/step3', data),
  onboardingStep4: (data) => api.post('/garage/onboarding/step4', data),
  onboardingStep5: (data) => api.post('/garage/onboarding/step5', data),
  getLeads: (status) => api.get('/garage/leads', { params: { status_filter: status } }),
  respondToLead: (postId, offer) => api.post(`/garage/leads/${postId}/respond`, offer),
  getBookings: (status) => api.get('/garage/bookings', { params: { status_filter: status } }),
  confirmBooking: (id) => api.put(`/garage/bookings/${id}/confirm`),
  updateBookingStatus: (id, status, notes) => api.put(`/garage/bookings/${id}/status`, { status, notes }),
  createQuotation: (bookingId, quotation) => api.post(`/garage/bookings/${bookingId}/quotation`, quotation),
  getJobCards: (status) => api.get('/garage/jobs', { params: { status_filter: status } }),
  getJobCard: (bookingId) => api.get(`/garage/jobs/${bookingId}`),
  createJobCard: (bookingId, job) => api.post(`/garage/jobs/${bookingId}`, job),
  updateJobCard: (bookingId, job) => api.put(`/garage/jobs/${bookingId}`, job),
  addJobUpdate: (bookingId, update) => api.post(`/garage/jobs/${bookingId}/update`, update),
  getAnalytics: () => api.get('/garage/analytics'),
  getDashboardSeries: (days = 30) => api.get('/garage/dashboard/series', { params: { days } }),
};

// Chats API
export const chatsAPI = {
  list: () => api.get('/chats'),
  get: (id) => api.get(`/chats/${id}`),
  sendMessage: (id, content, attachments) => api.post(`/chats/${id}/messages`, { content, attachments }),
  markRead: (id) => api.put(`/chats/${id}/read`),
  start: (type, referenceId, garageId) => api.post('/chats/start', null, { params: { type, reference_id: referenceId, garage_id: garageId } }),
};

// Reviews API (request/RFQ flow — garages & dealers)
export const reviewsAPI = {
  create: (review) => api.post('/reviews', review),
  getPending: () => api.get('/reviews/pending'),
  getMy: () => api.get('/reviews/my'),
  getProviderReviews: (providerId) => api.get(`/reviews/provider/${providerId}`),
  respond: (id, response) => api.post(`/reviews/${id}/respond`, { response }),
};

// Request Outcome Lifecycle API (provider_selected → job_completed/parts_delivered → customer_confirmed)
export const requestOutcomesAPI = {
  selectProvider: (requestId, { response_id, provider_type }) =>
    api.post(`/requests/${requestId}/select-provider`, { response_id, provider_type }),
  markComplete: (requestId) => api.post(`/provider/requests/${requestId}/mark-complete`),
  confirmCompletion: (requestId) => api.post(`/requests/${requestId}/confirm-completion`),
  noneSelected: (requestId, reason) => api.post(`/requests/${requestId}/none-selected`, { reason }),
};

// Garage Job lifecycle API (Phase A — post-selection accept/decline/timeline)
export const garageJobsAPI = {
  byRequest: (requestId) => api.get(`/garage-jobs/by-request/${requestId}`),
  get: (jobId) => api.get(`/garage-jobs/${jobId}`),
  pendingForGarage: (includeCompleted = false) => api.get('/garage/pending-jobs', { params: { include_completed: includeCompleted } }),
  pdf: (jobId) => api.get(`/garage-jobs/${jobId}/pdf`, { responseType: 'blob' }),
  accept: (jobId) => api.post(`/garage-jobs/${jobId}/accept`),
  decline: (jobId, reason, note) => api.post(`/garage-jobs/${jobId}/decline`, { reason, note }),
  reopen: (jobId, reason, note) => api.post(`/garage-jobs/${jobId}/reopen`, { reason, note }),
  requestRevocation: (jobId, reason) => api.post(`/garage-jobs/${jobId}/revocation/request`, { reason }),
  respondRevocation: (jobId, accept, note) => api.post(`/garage-jobs/${jobId}/revocation/respond`, { accept, note }),
  milestone: (jobId, milestone) => api.post(`/garage-jobs/${jobId}/milestone`, { milestone }),
  submitDiagnosisQuote: (jobId, payload) => api.post(`/garage-jobs/${jobId}/diagnosis-quote`, payload),
  decideDiagnosisQuote: (jobId, approve, note) => api.post(`/garage-jobs/${jobId}/diagnosis-quote/decision`, { approve, note }),
  dealerReviews: (jobId) => api.get(`/garage-jobs/${jobId}/dealer-reviews`),
  submitDealerReview: (jobId, payload) => api.post(`/garage-jobs/${jobId}/dealer-reviews`, payload),
};

// Public homepage success counters
export const statsAPI = {
  public: () => api.get('/stats/public'),
};

// Service Requests API (VIN-first flow)
export const requestsAPI = {
  create: (data) => api.post('/requests', data),
  list: (params) => api.get('/requests', { params }),
  get: (id) => api.get(`/requests/${id}`),
  getMatches: (id) => api.get(`/requests/${id}/matches`),
  getResponses: (id) => api.get(`/requests/${id}/responses`),
  send: (id, providerIds) => api.post(`/requests/${id}/send`, providerIds ? { provider_ids: providerIds } : null),
  getDashboardSeries: (days = 30) => api.get('/requests/dashboard/series', { params: { days } }),
  // Dealer quote view for request owners
  getQuotes: (id) => api.get(`/dealer/requests/${id}/quotes`),
  // Provider endpoints
  getProviderLeads: (params) => api.get('/requests/provider/leads', { params }),
  respondToRequest: (requestId, data) => api.post(`/requests/provider/respond/${requestId}`, data),
};

// Dealer API
export const dealerAPI = {
  getIncomingRequests: (params) => api.get('/dealer/requests', { params }),
  getRequestDetail: (id) => api.get(`/dealer/requests/${id}`),
  submitQuote: (requestId, data) => api.post(`/dealer/requests/${requestId}/quote`, data),
  unlockContact: (requestId) => api.post(`/dealer/requests/${requestId}/unlock-contact`),
  getRequestQuotes: (requestId) => api.get(`/dealer/requests/${requestId}/quotes`),
  getDashboardSeries: (days = 30) => api.get('/dealer/dashboard/series', { params: { days } }),
};

// Garage API (Phase 7 - Repair Responses)
export const garageAPI = {
  getIncomingRepairRequests: () => api.get('/garage/requests'),
  getRepairRequestDetail: (id) => api.get(`/garage/requests/${id}`),
  submitRepairResponse: (requestId, data) => api.post(`/garage/requests/${requestId}/respond`, data),
  unlockContact: (requestId) => api.post(`/garage/requests/${requestId}/unlock-contact`),
  getRepairResponses: (requestId) => api.get(`/garage/requests/${requestId}/responses`),
};

// Content Pages API (Phase 10)
export const contentAPI = {
  // Admin - Pages
  listPages: (params) => api.get('/admin/pages', { params }),
  getPage: (id) => api.get(`/admin/pages/${id}`),
  createPage: (data) => api.post('/admin/pages', data),
  updatePage: (id, data) => api.put(`/admin/pages/${id}`, data),
  deletePage: (id) => api.delete(`/admin/pages/${id}`),
  // Admin - UI Texts
  listUITexts: (params) => api.get('/admin/ui-texts', { params }),
  createUIText: (data) => api.post('/admin/ui-texts', data),
  updateUIText: (id, data) => api.put(`/admin/ui-texts/${id}`, data),
  deleteUIText: (id) => api.delete(`/admin/ui-texts/${id}`),
  // Admin - SEO Meta
  listSEOMeta: () => api.get('/admin/seo-meta'),
  createSEOMeta: (data) => api.post('/admin/seo-meta', data),
  updateSEOMeta: (id, data) => api.put(`/admin/seo-meta/${id}`, data),
  deleteSEOMeta: (id) => api.delete(`/admin/seo-meta/${id}`),
  // Public
  getPublicPage: (slug) => api.get(`/pages/${slug}`),
  getPublicUITexts: (lang) => api.get('/ui-texts', { params: { language: lang || 'en' } }),
  getPublicSEOMeta: (route) => api.get(`/seo-meta/${route}`),
};

export const menusAPI = {
  // Admin
  listItems: (menu_type) => api.get('/admin/menu-items', { params: menu_type ? { menu_type } : {} }),
  createItem: (data) => api.post('/admin/menu-items', data),
  updateItem: (id, data) => api.put(`/admin/menu-items/${id}`, data),
  deleteItem: (id) => api.delete(`/admin/menu-items/${id}`),
  reorder: (menu_type, item_ids) => api.post(`/admin/menu-items/${menu_type}/reorder`, { item_ids }),
  // Public
  getPublic: (menu_type) => api.get(`/menus/${menu_type}`),
};

// Vehicle Profiles API (VIN Intelligence)
export const vehicleProfilesAPI = {
  decodeVin: (vin, forceRefresh = false) => api.post('/vehicles/decode-vin', { vin, force_refresh: forceRefresh }),
  getProfile: (id) => api.get(`/vehicles/profile/${id}`),
  confirmProfile: (id, overrides) => api.post(`/vehicles/profile/${id}/confirm`, { user_overrides: overrides, is_user_confirmed: true }),
  refreshProfile: (id) => api.post(`/vehicles/profile/${id}/refresh`),
  listMyProfiles: (confirmedOnly = false) => api.get('/vehicles/my-profiles', { params: { confirmed_only: confirmedOnly } }),
};

// Admin Vehicle API Settings
export const vehicleApiSettingsAPI = {
  list: () => api.get('/admin/settings/vehicle-apis'),
  get: (id) => api.get(`/admin/settings/vehicle-apis/${id}`),
  update: (id, data) => api.put(`/admin/settings/vehicle-apis/${id}`, data),
  create: (data) => api.post('/admin/settings/vehicle-apis', data),
  test: (id) => api.post(`/admin/settings/vehicle-apis/${id}/test`),
};


// Masters API - add parts categories
mastersAPI.getPartsCategories = () => api.get('/masters/parts-categories');

// Admin API
export const adminAPI = {
  getDashboard: () => api.get('/admin/dashboard'),
  getEnhancedDashboard: () => api.get('/admin/dashboard/enhanced'),
  getDashboardSeries: (days = 30) => api.get('/admin/dashboard/series', { params: { days } }),
  
  // Users
  getUsers: (params) => api.get('/admin/users', { params }),
  updateUserStatus: (id, status) => api.put(`/admin/users/${id}/status`, null, { params: { new_status: status } }),
  
  // Providers (unified)
  getProviders: (params) => api.get('/admin/providers', { params }),
  getPendingProviders: (params) => api.get('/admin/providers/pending', { params }),
  getProviderDetails: (id) => api.get(`/admin/providers/${id}`),
  verifyProvider: (id, action, reason) => api.put(`/admin/providers/${id}/verify`, null, { params: { action, reason } }),
  updateProviderSubscription: (id, status, plan, reason) => api.put(`/admin/providers/${id}/subscription`, null, { 
    params: { subscription_status: status, subscription_plan: plan, reason } 
  }),
  
  // Legacy garage endpoints (keep for backwards compat)
  getGarages: (params) => api.get('/admin/garages', { params }),
  getPendingVerifications: (page) => api.get('/admin/garages/pending', { params: { page } }),
  getGarageDetails: (id) => api.get(`/admin/garages/${id}`),
  verifyGarage: (id, action, reason) => api.put(`/admin/garages/${id}/verify`, null, { params: { action, reason } }),
  
  // Service Requests
  getRequests: (params) => api.get('/admin/requests', { params }),
  getRequestDetails: (id) => api.get(`/admin/requests/${id}`),
  getCloseReasonsSummary: () => api.get('/admin/requests/close-reasons-summary'),
  getVehicleSystemStatus: () => api.get('/admin/vehicle-system-status'),
  
  // Master Data
  getCategories: () => api.get('/admin/categories'),
  createCategory: (name, icon) => api.post('/admin/categories', null, { params: { name, icon } }),
  getPartsCategories: () => api.get('/admin/parts-categories'),
  createPartsCategory: (name, icon) => api.post('/admin/parts-categories', null, { params: { name, icon } }),
  getMakes: () => api.get('/admin/makes'),
  createMake: (name, type) => api.post('/admin/makes', null, { params: { name, type } }),
  getModels: (makeId) => api.get('/admin/models', { params: { make_id: makeId } }),
  createModel: (makeId, name) => api.post('/admin/models', null, { params: { make_id: makeId, name } }),
  getCountries: () => api.get('/admin/countries'),
  getCities: (countryCode) => api.get('/admin/cities', { params: { country_code: countryCode } }),
  createCity: (countryCode, name, nameAr) => api.post('/admin/cities', null, { params: { country_code: countryCode, name, name_ar: nameAr } }),
  
  // Audit Logs
  getAuditLogs: (params) => api.get('/admin/audit-logs', { params }),
  
  // Legacy
  getBookings: (params) => api.get('/admin/bookings', { params }),
  getReports: () => api.get('/admin/reports'),  
  // Settings (Legacy)
  getSettings: () => api.get('/admin/settings'),
  updateSettings: (settings) => api.put('/admin/settings', settings),
  
  // System Settings (New)
  getSystemSettings: () => api.get('/settings'),
  updateSystemSettings: (settings) => api.put('/settings', settings),
  
  // Provider Management
  createProvider: (data) => api.post('/admin/providers/create', data),
  updateProviderKYCStatus: (id, status, reason) => api.put(`/admin/providers/${id}/kyc-status`, null, { 
    params: { status, reason } 
  }),
  assignProviderPlan: (providerId, planId) => api.post(`/admin/providers/${providerId}/assign-plan`, null, { 
    params: { plan_id: planId } 
  }),
  
  // Pricing Plans — legacy admin CRUD removed in Phase P2.
  // Use monetizationAPI.* for the canonical /admin/monetization/plans CRUD.
  
  // Currencies
  getCurrencies: () => api.get('/billing/currencies?active_only=false'),
  updateCurrency: (code, data) => api.put(`/billing/currencies/${code}`, data),
  
  // Payments (C2)
  getPaymentTransactions: (params) => api.get('/payments/admin/transactions', { params }),
  getPaymentStats: () => api.get('/payments/admin/stats'),
  getPaymentEvents: (params) => api.get('/payments/admin/events', { params }),

  // Media Uploads (Admin → Media Uploads page)
  listUploads: (params) => api.get('/admin/uploads', { params }),
};

// Users API
export const usersAPI = {
  getProfile: () => api.get('/users/profile'),
  updateProfile: (data) => api.put('/users/profile', data),
  changePassword: (payload) => api.post('/users/change-password', payload),
  resendVerificationEmail: () => api.post('/auth/resend-verification-email'),
  deleteAccount: () => api.delete('/users/account'),
};

// Vehicle Ownership (U2)
export const vehicleOwnershipAPI = {
  updateOwnership: (vehicleId, payload) => api.put(`/vehicles/${vehicleId}/ownership`, payload),
  submitClaim: (payload) => api.post('/vehicle-ownership/claims', payload),
  myClaims: (params) => api.get('/vehicle-ownership/claims/mine', { params }),
  withdrawClaim: (claimId) => api.post(`/vehicle-ownership/claims/${claimId}/withdraw`),
};

// Admin Vehicle Ownership (U2)
export const adminVehicleOwnershipAPI = {
  listClaims: (params) => api.get('/admin/vehicle-ownership/claims', { params }),
  getClaim: (id) => api.get(`/admin/vehicle-ownership/claims/${id}`),
  approveClaim: (id) => api.post(`/admin/vehicle-ownership/claims/${id}/approve`),
  rejectClaim: (id, reason) => api.post(`/admin/vehicle-ownership/claims/${id}/reject`, { reason }),
  listEvents: (params) => api.get('/admin/vehicle-ownership/events', { params }),
  ownershipTimeline: (vehicleId) => api.get(`/admin/vehicle-ownership/timeline/${vehicleId}`),
};

// Customer Products (M-B — Admin CRUD on `vehicle_care_plans`).
// Backed by /api/vehicle-care/admin/* (admin-only). The customer-facing
// read endpoint /api/vehicle-care/plans remains unchanged.
export const customerProductsAPI = {
  list: (params) => api.get('/vehicle-care/admin/plans', { params }),
  get: (id) => api.get(`/vehicle-care/admin/plans/${id}`),
  create: (body) => api.post('/vehicle-care/admin/plans', body),
  update: (id, body) => api.put(`/vehicle-care/admin/plans/${id}`, body),
  archive: (id) => api.post(`/vehicle-care/admin/plans/${id}/archive`),
  duplicate: (id) => api.post(`/vehicle-care/admin/plans/${id}/duplicate`),
  reconcileStripe: (id) => api.post(`/vehicle-care/admin/plans/${id}/reconcile-stripe`),
};

// Vehicle Care+ (U3)
export const vehicleCareAPI = {
  listPlans: () => api.get('/vehicle-care/plans'),
  getPlan: (code) => api.get(`/vehicle-care/plans/${code}`),
  enroll: (payload) => api.post('/vehicle-care/enroll', payload),
  mySubscriptions: (params) => api.get('/vehicle-care/my-subscriptions', { params }),
  getSubscription: (id) => api.get(`/vehicle-care/my-subscriptions/${id}`),
  cancelSubscription: (id) => api.delete(`/vehicle-care/my-subscriptions/${id}`),
  // Phase 2 — Companion
  careDashboard: () => api.get('/vehicle-care/dashboard'),
  reminders: () => api.get('/vehicle-care/reminders'),
  getProfile: (vehicleId) => api.get(`/vehicle-care/profile/${vehicleId}`),
  setRegistration: (vehicleId, data) => api.put(`/vehicle-care/profile/${vehicleId}/registration`, data),
  setInsurance: (vehicleId, data) => api.put(`/vehicle-care/profile/${vehicleId}/insurance`, data),
  setInspection: (vehicleId, data) => api.put(`/vehicle-care/profile/${vehicleId}/inspection`, data),
  setWarranty: (vehicleId, data) => api.put(`/vehicle-care/profile/${vehicleId}/warranty`, data),
  addMileage: (vehicleId, mileage) => api.post(`/vehicle-care/profile/${vehicleId}/mileage`, { mileage }),
  addMaintenanceItem: (vehicleId, data) => api.post(`/vehicle-care/profile/${vehicleId}/maintenance-items`, data),
  deleteMaintenanceItem: (vehicleId, itemId) => api.delete(`/vehicle-care/profile/${vehicleId}/maintenance-items/${itemId}`),
  setReminderPreferences: (vehicleId, data) => api.put(`/vehicle-care/profile/${vehicleId}/reminder-preferences`, data),
  downloadReport: (vehicleId, period) => api.get(`/vehicle-care/profile/${vehicleId}/report`, { params: { period }, responseType: 'blob' }),
  getServiceConfig: () => api.get('/vehicle-care/admin/service-config'),
  setServiceConfig: (data) => api.put('/vehicle-care/admin/service-config', data),
  reminderRunsLatest: () => api.get('/vehicle-care/admin/reminder-runs/latest'),
  reminderRunNow: () => api.post('/vehicle-care/admin/reminder-runs/run-now'),
};

// Customer Billing (S4 — live Stripe recurring checkout)
export const customerBillingAPI = {
  status: () => api.get('/customer-billing/status'),
  listInvoices: (params) => api.get('/customer-billing/invoices', { params }),
  getInvoice: (id) => api.get(`/customer-billing/invoices/${id}`),
  checkoutVehicleCare: (subscriptionId) =>
    api.post('/customer-billing/checkout/vehicle-care', {
      subscription_id: subscriptionId,
      origin_url: window.location.origin,
    }),
  paymentStatus: (sessionId) => api.get(`/customer-billing/payment-status/${sessionId}`),
  emailInvoice: (recordId) => api.post(`/customer-billing/invoices/${recordId}/email`),
};

// Garage → Dealer RFQ (parts sourcing) — garage-facing surface
export const garageRfqAPI = {
  list: () => api.get('/garage/rfq/list'),
  repairVehicles: () => api.get('/garage/rfq/repair-vehicles'),
  detail: (id) => api.get(`/garage/rfq/${id}`),
  resolve: (id, data) => api.post(`/garage/rfq/${id}/resolve`, data || undefined),
  refineText: (text) => api.post('/garage/rfq/refine-text', { text }),
  selectDealer: (id, quoteId) => api.post(`/garage/rfq/${id}/dealers/select`, { quote_id: quoteId }),
  unselectDealer: (selectionId) => api.delete(`/garage/rfq/dealers/${selectionId}`),
  togglePartsReceived: (selectionId) => api.post(`/garage/rfq/dealers/${selectionId}/toggle-received`),
};

// Dealer → Dealer RFQ (D2D parts sourcing) — requesting-dealer surface
export const dealerRfqAPI = {
  create: (payload) => api.post('/dealer/rfq/create', payload),
  list: () => api.get('/dealer/rfq/list'),
  detail: (id) => api.get(`/dealer/rfq/${id}`),
  resolve: (id) => api.post(`/dealer/rfq/${id}/resolve`),
  refineText: (text) => api.post('/dealer/rfq/refine-text', { text }),
};

// Admin — Customer Billing (Vehicle Care+ records + refunds)
export const adminCustomerBillingAPI = {
  listRecords: (params) => api.get('/admin/customer-billing/records', { params }),
  getRecord: (id) => api.get(`/admin/customer-billing/records/${id}`),
  refund: (id) => api.post(`/admin/customer-billing/records/${id}/refund`),
};

// Uploads API
export const uploadsAPI = {
  uploadImage: (file, context = 'general') => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/uploads/image?context=${context}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  uploadImages: (files, context = 'general') => {
    const formData = new FormData();
    files.forEach(file => formData.append('files', file));
    return api.post(`/uploads/images?context=${context}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  uploadDocument: (file, context = 'kyc') => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/uploads/document?context=${context}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  deleteFile: (filename) => api.delete(`/uploads/files/${filename}`),
};

// P8-P1.6 — Design System administration workflow (draft/publish/rollback)
export const designSystemAPI = {
  get: () => api.get('/settings/design-system'),
  saveDraft: (payload) => api.put('/settings/design-system/draft', payload),
  publish: () => api.post('/settings/design-system/publish'),
  versions: () => api.get('/settings/design-system/versions'),
  rollback: (version) => api.post(`/settings/design-system/rollback/${version}`),
  killSwitch: (enabled) => api.put('/settings/design-system/kill-switch', { enabled }),
};


// Provider API (unified for garage and dealer)
export const providerAPI = {
  getProfile: () => api.get('/provider/profile'),
  updateProfile: (data) => api.put('/provider/profile', data),
  updateSpecializations: (data) => api.put('/provider/profile/specializations', data),
  updateMakes: (data) => api.put('/provider/profile/makes', data),
  updateWorkingHours: (data) => api.put('/provider/profile/working-hours', data),
  updateBranch: (data) => api.put('/provider/profile/branch', data),
  updateDocuments: (data) => api.put('/provider/profile/documents', data),
  getKYCStatus: () => api.get('/provider/profile/kyc-status'),
  onboardingStep1: (data) => api.post('/provider/onboarding/step1', data),
  searchBusinesses: (q) => api.get('/provider/onboarding/search-businesses', { params: { q } }),
  onboardManual: (data) => api.post('/provider/onboarding/manual', data),
  onboardingStep2: (providerType, data) => api.post(`/provider/onboarding/step2?provider_type=${providerType}`, data),
  onboardingStep3: (providerType, data) => api.post(`/provider/onboarding/step3?provider_type=${providerType}`, data),
  onboardingStep4: (providerType, data) => api.post(`/provider/onboarding/step4?provider_type=${providerType}`, data),
  onboardingStep5: (providerType, data) => api.post(`/provider/onboarding/step5?provider_type=${providerType}`, data),
  getLeads: (providerType, params) => api.get(`/provider/${providerType}/leads`, { params }),
  respondToLead: (providerType, requestId, data) => api.post(`/provider/${providerType}/leads/${requestId}/respond`, data),
  // Location APIs
  getLocationsForMap: (params) => api.get('/provider/locations/map', { params }),
  getNearbyProviders: (lat, lng, radiusKm, providerType) => api.get('/provider/locations/nearby', { 
    params: { lat, lng, radius_km: radiusKm, provider_type: providerType } 
  }),
};

// Specializations API
export const specializationsAPI = {
  getGarageSpecializations: () => api.get('/specializations/garage'),
  getDealerPartsCategories: () => api.get('/specializations/dealer/parts-categories'),
  getDealerInventoryTypes: () => api.get('/specializations/dealer/inventory-types'),
  getAll: () => api.get('/specializations/all'),
};

// Master Data API
export const masterDataAPI = {
  // Vehicle Types (DB-managed)
  getVehicleTypes: () => api.get('/master-data/vehicle-types'),
  getAllVehicleTypes: () => api.get('/master-data/vehicle-types/all'),
  createVehicleType: (data) => api.post('/master-data/vehicle-types', data),
  updateVehicleType: (id, data) => api.put(`/master-data/vehicle-types/${id}`, data),
  deleteVehicleType: (id) => api.delete(`/master-data/vehicle-types/${id}`),
  // Fuel Types (NEW)
  getFuelTypes: () => api.get('/master-data/fuel-types'),
  getAllFuelTypes: () => api.get('/master-data/fuel-types/all'),
  createFuelType: (data) => api.post('/master-data/fuel-types', data),
  updateFuelType: (id, data) => api.put(`/master-data/fuel-types/${id}`, data),
  deleteFuelType: (id) => api.delete(`/master-data/fuel-types/${id}`),
  // Vehicle Makes
  getVehicleMakes: (params) => api.get('/master-data/vehicle-makes', { params }),
  createVehicleMake: (data) => api.post('/master-data/vehicle-makes', data),
  updateVehicleMake: (id, data) => api.put(`/master-data/vehicle-makes/${id}`, data),
  deleteVehicleMake: (id) => api.delete(`/master-data/vehicle-makes/${id}`),
  // Motorcycle Makes
  getMotorcycleMakes: (params) => api.get('/master-data/motorcycle-makes', { params }),
  // Vehicle Models (NEW)
  getVehicleModels: (params) => api.get('/master-data/vehicle-models', { params }),
  syncModelsForMake: (makeId) => api.post(`/admin/vehicle-catalogue/makes/${encodeURIComponent(makeId)}/models/sync`),
  createVehicleModel: (data) => api.post('/master-data/vehicle-models', data),
  updateVehicleModel: (id, data) => api.put(`/master-data/vehicle-models/${id}`, data),
  deleteVehicleModel: (id) => api.delete(`/master-data/vehicle-models/${id}`),
  // Service Categories
  getServiceCategories: () => api.get('/master-data/service-categories'),
  getServiceCategoriesGrouped: () => api.get('/master-data/service-categories/grouped'),
  createServiceCategory: (data) => api.post('/master-data/service-categories', data),
  updateServiceCategory: (id, data) => api.put(`/master-data/service-categories/${id}`, data),
  deleteServiceCategory: (id) => api.delete(`/master-data/service-categories/${id}`),
  // Parts Categories
  getPartsCategories: () => api.get('/master-data/parts-categories'),
  getPartsCategoriesGrouped: () => api.get('/master-data/parts-categories/grouped'),
  createPartsCategory: (data) => api.post('/master-data/parts-categories', data),
  updatePartsCategory: (id, data) => api.put(`/master-data/parts-categories/${id}`, data),
  deletePartsCategory: (id) => api.delete(`/master-data/parts-categories/${id}`),
  // Countries
  getCountries: () => api.get('/master-data/countries'),
  deleteCountry: (code, force = false) => api.delete(`/master-data/countries/${code}`, { params: { force } }),
  // Cities
  getCities: (countryCode) => api.get('/master-data/cities', { params: { country_code: countryCode } }),
  createCity: (data) => api.post('/master-data/cities', data),
  updateCity: (id, data) => api.put(`/master-data/cities/${id}`, data),
  deleteCity: (id) => api.delete(`/master-data/cities/${id}`),
  // Areas
  getAreas: (cityId) => api.get('/master-data/areas', { params: { city_id: cityId } }),
  createArea: (data) => api.post('/master-data/areas', data),
  // Import / Export / Clear
  downloadVehicleTemplate: () => api.get('/master-data/vehicle-import/template', { responseType: 'blob' }),
  importVehicleData: (file, mode) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/master-data/vehicle-import?mode=${mode}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  downloadBulkTemplate: (dataset) => api.get(`/master-data/bulk/${dataset}/template`, { responseType: 'blob' }),
  exportBulkData: (dataset) => api.get(`/master-data/bulk/${dataset}/export`, { responseType: 'blob' }),
  importBulkData: (dataset, file, mode, preview = false, previewDigest = null) => {
    const formData = new FormData();
    formData.append('file', file);
    const digestQuery = previewDigest ? `&preview_digest=${encodeURIComponent(previewDigest)}` : '';
    return api.post(`/master-data/bulk/${dataset}/import?mode=${mode}&preview=${preview}${digestQuery}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  clearBulkData: (dataset, force = false) => api.delete(`/master-data/bulk/${dataset}`, { params: { force } }),
  // KYC
  getKYCDocumentTypes: () => api.get('/master-data/kyc-document-types'),
  // Summary
  getSummary: () => api.get('/master-data/summary'),
};

// Admin Users API
export const adminUsersAPI = {
  list: (params) => api.get('/admin/users', { params }),
  getStats: () => api.get('/admin/users/stats'),
  get: (id) => api.get(`/admin/users/${id}`),
  create: (data) => api.post('/admin/users', data),
  update: (id, data) => api.put(`/admin/users/${id}`, data),
  updateStatus: (id, status, reason) => api.put(`/admin/users/${id}/status`, null, { params: { status, reason } }),
  delete: (id, permanent) => api.delete(`/admin/users/${id}`, { params: { permanent } }),
  getRoles: () => api.get('/admin/users/roles/list'),
};

// Callbacks API
export const callbacksAPI = {
  create: (data) => api.post('/callbacks', data),
  listMy: () => api.get('/callbacks/my'),
  listGarage: (status) => api.get('/callbacks/garage', { params: { status_filter: status } }),
  get: (id) => api.get(`/callbacks/${id}`),
  updateStatus: (id, status, notes) => api.put(`/callbacks/${id}`, { status, notes }),
};

// Billing API
export const billingAPI = {
  // Plans
  getPlans: (activeOnly = true) => api.get('/billing/plans', { params: { active_only: activeOnly } }),
  getPlan: (id) => api.get(`/billing/plans/${id}`),
  
  // Currencies
  getCurrencies: (activeOnly = true) => api.get('/billing/currencies', { params: { active_only: activeOnly } }),
  convertPrice: (amountAed, targetCurrency) => 
    api.get('/billing/convert-price', { params: { amount_aed: amountAed, target_currency: targetCurrency } }),
  
  // Subscriptions
  getCurrentSubscription: () => api.get('/billing/subscriptions/current'),
  subscribe: (planId, currencyCode = 'AED') => 
    api.post(`/billing/subscriptions/subscribe?plan_id=${planId}&currency_code=${currencyCode}`),
  
  // Transactions
  getTransactions: (skip = 0, limit = 20) => 
    api.get('/billing/transactions', { params: { skip, limit } }),
};

// Provider KYC API (Phase 11)
export const kycAPI = {
  getStatus: () => api.get('/provider/kyc/status'),
  submitBusiness: (data) => api.post('/provider/kyc/business', data),
  createIdentity: (data) => api.post('/provider/kyc/identities', data),
  updateIdentity: (id, data) => api.put(`/provider/kyc/identities/${id}`, data),
  deleteIdentity: (id) => api.delete(`/provider/kyc/identities/${id}`),
  createBranch: (data) => api.post('/provider/kyc/branches', data),
  updateBranch: (id, data) => api.put(`/provider/kyc/branches/${id}`, data),
  deleteBranch: (id) => api.delete(`/provider/kyc/branches/${id}`),
  // UAT-5: renewal
  renew: (kind, id) => api.post(`/provider/kyc/${kind}/${id}/renew`),
};

// UAT-5: admin set-expiry for KYC docs
export const kycAdminAPI = {
  setExpiry: (kind, id, expires_at) =>
    api.post(`/admin/kyc/${kind}/${id}/set-expiry`, { expires_at }),
};

// Admin KYC Review API (Phase 11 + Optimization)
export const adminKycAPI = {
  getQueue: (status, assignment) => api.get('/admin/kyc/queue', { params: { status, assignment } }),
  getDetail: (providerId) => api.get(`/admin/kyc/${providerId}`),
  reviewBusiness: (providerId, data) => api.post(`/admin/kyc/${providerId}/business/review`, data),
  reviewIdentity: (identityId, data) => api.post(`/admin/kyc/identities/${identityId}/review`, data),
  reviewBranch: (branchId, data) => api.post(`/admin/kyc/branches/${branchId}/review`, data),
  assign: (providerId, moderatorId) => api.post(`/admin/kyc/${providerId}/assign`, { moderator_id: moderatorId }),
  getModerators: () => api.get('/admin/kyc/moderators'),
};

// Subscription API (C2 - Unified)
export const subscriptionAPI = {
  getStatus: () => api.get('/subscription/status'),
  createCheckoutSession: (data) => api.post('/subscription/create-checkout-session', data),
  getCheckoutStatus: (sessionId) => api.get(`/subscription/checkout-status/${sessionId}`),
  cancel: () => api.post('/subscription/cancel'),
};

// Payments API (C2 - Admin)
export const paymentsAPI = {
  getStatus: (sessionId) => api.get(`/payments/status/${sessionId}`),
};

// Admin Payments API (C2)
export const adminPaymentsAPI = {
  getTransactions: (params) => api.get('/payments/admin/transactions', { params }),
  getStats: () => api.get('/payments/admin/stats'),
  getEvents: (params) => api.get('/payments/admin/events', { params }),
};

// Integration Settings API
export const integrationsAPI = {
  list: () => api.get('/settings/integrations'),
  update: (providerId, data) => api.put(`/settings/integrations/${providerId}`, data),
  test: (providerId) => api.post(`/settings/integrations/${providerId}/test`),
};

// Maps API
export const mapsAPI = {
  getConfig: () => api.get('/maps/config'),
  autocomplete: (q, country) => api.get('/maps/autocomplete', { params: { q, country } }),
  geocode: (address) => api.get('/maps/geocode', { params: { address } }),
  placeDetails: (placeId) => api.get('/maps/place-details', { params: { place_id: placeId } }),
  reverseGeocode: (lat, lng) => api.get('/maps/reverse-geocode', { params: { lat, lng } }),
  nearbyProviders: (lat, lng, radiusKm, providerType) =>
    api.get('/maps/nearby-providers', { params: { lat, lng, radius_km: radiusKm, provider_type: providerType } }),
};

// FC1 — generic map discovery data plane (platform capability).
// Layer-discriminated; future layers reuse the same envelope:
//   layer="unclaimed_leads" | "verified_providers" | "open_requests" | …
export const mapAPI = {
  discover: ({ layer = 'unclaimed_leads', bbox, category, q, limit = 200, cursor } = {}) =>
    api.get('/map/discover', { params: { layer, bbox, category, q, limit, cursor } }),
  heatmap: ({ layer = 'unclaimed_leads' } = {}) =>
    api.post('/map/heatmap', { layer }),
};


// Monetization API (Phase M1)
export const monetizationAPI = {
  // Plans
  listPlans: (params) => api.get('/admin/monetization/plans', { params }),
  getPlan: (id) => api.get(`/admin/monetization/plans/${id}`),
  createPlan: (body) => api.post('/admin/monetization/plans', body),
  updatePlan: (id, body) => api.put(`/admin/monetization/plans/${id}`, body),
  archivePlan: (id) => api.post(`/admin/monetization/plans/${id}/archive`),
  duplicatePlan: (id) => api.post(`/admin/monetization/plans/${id}/duplicate`),
  reconcilePlanStripe: (id) => api.post(`/admin/monetization/plans/${id}/reconcile-stripe`),

  // Subscriptions
  listSubscriptions: (params) => api.get('/admin/monetization/subscriptions', { params }),
  getSubscription: (id) => api.get(`/admin/monetization/subscriptions/${id}`),
  assignPlan: (id, body) => api.post(`/admin/monetization/subscriptions/${id}/assign-plan`, body),
  extend: (id, body) => api.post(`/admin/monetization/subscriptions/${id}/extend`, body),
  resetUsage: (id) => api.post(`/admin/monetization/subscriptions/${id}/reset-usage`),
  cancel: (id, body = {}) => api.post(`/admin/monetization/subscriptions/${id}/cancel`, body),
  setGrace: (id, body) => api.post(`/admin/monetization/subscriptions/${id}/set-grace`, body),

  // Usage
  listUsage: (params) => api.get('/admin/monetization/usage', { params }),
  listUsageEvents: (providerId, params) => api.get(`/admin/monetization/usage/${providerId}/events`, { params }),
  adjustUsage: (providerId, body) => api.post(`/admin/monetization/usage/${providerId}/adjust`, body),

  // Billing
  billingOverview: () => api.get('/admin/monetization/billing/overview'),
  upgradePressure: (params) => api.get('/admin/monetization/billing/upgrade-pressure', { params }),
};

// Admin Billing API (Phase M2-B) — read-only access to billing orders/invoices/credit notes
export const adminBillingAPI = {
  // Billing orders
  listOrders: (params) => api.get('/admin/billing/orders', { params }),
  getOrder: (id) => api.get(`/admin/billing/orders/${id}`),
  // Invoices
  listInvoices: (params) => api.get('/admin/billing/invoices', { params }),
  getInvoice: (id) => api.get(`/admin/billing/invoices/${id}`),
  // Fetch HTML (authenticated). Returns a Blob URL ready for window.open() or download.
  fetchInvoiceHtml: async (id) => {
    const res = await api.get(`/admin/billing/invoices/${id}/html`, { responseType: 'text' });
    return new Blob([res.data], { type: 'text/html;charset=utf-8' });
  },
  // M3: PDF + Email
  fetchInvoicePdf: async (id) => {
    const res = await api.get(`/admin/billing/invoices/${id}/pdf`, { responseType: 'blob' });
    return res.data;
  },
  emailInvoice: (id, recipient_email = null) =>
    api.post(`/admin/billing/invoices/${id}/email`, recipient_email ? { recipient_email } : {}),
  // Credit notes
  listCreditNotes: (params) => api.get('/admin/billing/credit-notes', { params }),
  getCreditNote: (id) => api.get(`/admin/billing/credit-notes/${id}`),
  fetchCreditNoteHtml: async (id) => {
    const res = await api.get(`/admin/billing/credit-notes/${id}/html`, { responseType: 'text' });
    return new Blob([res.data], { type: 'text/html;charset=utf-8' });
  },
  fetchCreditNotePdf: async (id) => {
    const res = await api.get(`/admin/billing/credit-notes/${id}/pdf`, { responseType: 'blob' });
    return res.data;
  },
  emailCreditNote: (id, recipient_email = null) =>
    api.post(`/admin/billing/credit-notes/${id}/email`, recipient_email ? { recipient_email } : {}),
  // M3: email logs
  listEmailLogs: (params) => api.get('/admin/billing/email-logs', { params }),
};

// Helpers for M2-B UI — open or download an HTML blob
export const openHtmlBlob = (blob) => {
  const url = URL.createObjectURL(blob);
  window.open(url, '_blank', 'noopener,noreferrer');
  // Revoke after enough time for the new tab to parse
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
};
export const downloadHtmlBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
};

// Provider Monetization API (Phase M1-C)
export const providerMonetizationAPI = {
  listPlans: () => api.get('/provider/plans'),
  getSubscription: () => api.get('/provider/subscription'),
  getUsage: () => api.get('/provider/subscription/usage'),
  listUsageEvents: (params) => api.get('/provider/subscription/usage-events', { params }),
  changePlan: (planId) => api.post('/provider/subscription/change-plan', { plan_id: planId }),
  cancel: (reason, at_period_end = true) =>
    api.post('/provider/subscription/cancel', { reason: reason || null, at_period_end }),
  resume: () => api.post('/provider/subscription/resume'),
  billingPortal: () => api.post('/provider/subscription/billing-portal'),
};

// Provider Billing API (Phase M2-C) — provider sees own invoices + credit notes
export const providerBillingAPI = {
  listInvoices: (params) => api.get('/provider/billing/invoices', { params }),
  listCreditNotes: (params) => api.get('/provider/billing/credit-notes', { params }),
  fetchInvoiceHtml: async (id) => {
    const res = await api.get(`/provider/billing/invoices/${id}/html`, { responseType: 'text' });
    return new Blob([res.data], { type: 'text/html;charset=utf-8' });
  },
  fetchCreditNoteHtml: async (id) => {
    const res = await api.get(`/provider/billing/credit-notes/${id}/html`, { responseType: 'text' });
    return new Blob([res.data], { type: 'text/html;charset=utf-8' });
  },
  // M3: PDF + Email
  fetchInvoicePdf: async (id) => {
    const res = await api.get(`/provider/billing/invoices/${id}/pdf`, { responseType: 'blob' });
    return res.data;
  },
  emailInvoice: (id) => api.post(`/provider/billing/invoices/${id}/email`),
};

// Helper to download a blob with given filename (used for PDF)
export const downloadBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
};

// Notifications API (UAT-3A)
export const notificationsAPI = {
  list: (params) => api.get('/notifications', { params }),
  unreadCount: () => api.get('/notifications/unread-count'),
  markRead: (id) => api.put(`/notifications/${id}/read`),
  markAllRead: () => api.post('/notifications/read-all'),
  delete: (id) => api.delete(`/notifications/${id}`),
  clearAll: () => api.delete('/notifications'),
};

export const partsPurchaseHistoryAPI = {
  list: (vehicleId) => api.get('/parts-purchase-history', { params: vehicleId ? { vehicle_id: vehicleId } : {} }),
};

// Device Tokens API (foundation for UAT-3B push)
export const deviceTokensAPI = {
  register: (payload) => api.post('/device-tokens/register', payload),
  unregister: (token) => api.post('/device-tokens/unregister', { token }),
};

// Firebase Admin API (UAT-3B)
export const firebaseAdminAPI = {
  getConfig: () => api.get('/admin/firebase/config'),
  updateConfig: (payload) => api.put('/admin/firebase/config', payload),
  uploadServiceAccount: (file) => {
    const form = new FormData();
    form.append('file', file);
    return api.post('/admin/firebase/service-account', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  deleteServiceAccount: () => api.delete('/admin/firebase/service-account'),
  testPush: (payload) => api.post('/admin/firebase/test-push', payload),
  deviceStats: () => api.get('/admin/firebase/devices/stats'),
  diagnostics: () => api.get('/admin/firebase/diagnostics'),
  webConfig: () => api.get('/firebase/web-config'),
};
